import * as THREE from 'three';
import { PATHS as COUNTRY_PATHS } from './worldCoastline';

// Bitcoin-node-adoption hotspots used to weight country brightness.
// Inlined here (not pulled from useNodeData) so landmassTexture.js is a
// pure standalone utility — just paths, canvas, done.
// [lat, lon, relativeWeight]
const NODE_CITY_WEIGHTS = [
  // North America
  [40.71,  -74.00, 180], [37.77, -122.42, 120], [34.05, -118.25,  80],
  [41.88,  -87.63,  90], [47.61, -122.33,  70], [33.45, -112.07,  40],
  [29.76,  -95.36,  60], [25.76,  -80.19,  50], [39.95,  -75.17,  50],
  [32.72, -117.16,  40], [38.90,  -77.04,  50], [30.27,  -97.74,  60],
  [36.17, -115.14,  30], [43.65,  -79.38,  80], [45.50,  -73.57,  40],
  [49.28, -123.12,  50],
  // Europe
  [52.52,   13.41, 200], [50.11,    8.68, 110], [48.14,   11.58,  70],
  [53.55,    9.99,  60], [51.51,   -0.13, 140], [53.48,   -2.24,  40],
  [48.86,    2.35, 100], [52.37,    4.90, 120], [50.85,    4.35,  40],
  [59.33,   18.07,  70], [55.68,   12.57,  50], [60.17,   24.94,  50],
  [59.91,   10.75,  40], [47.37,    8.54,  60], [48.21,   16.37,  40],
  [45.46,    9.19,  40], [41.90,   12.50,  30], [40.42,   -3.70,  35],
  [41.38,    2.17,  30], [55.75,   37.62,  90], [59.93,   30.33,  40],
  [50.45,   30.52,  50], [52.23,   21.01,  30], [50.08,   14.43,  25],
  [44.43,   26.10,  20], [37.98,   23.73,  20], [41.01,   28.98,  30],
  [53.34,   -6.26,  25],
  // Asia-Pacific
  [35.69,  139.70, 130], [34.69,  135.50,  30], [37.56,  126.99,  90],
  [ 1.29,  103.85,  60], [22.31,  114.17,  50], [25.03,  121.57,  30],
  [31.23,  121.47,  40], [39.90,  116.40,  30], [22.54,  114.06,  20],
  [13.76,  100.50,  25], [14.60,  120.98,  15], [-6.21,  106.85,  20],
  [ 3.14,  101.69,  15], [28.61,   77.21,  35], [19.07,   72.88,  40],
  [12.97,   77.59,  40],
  // Oceania
  [-33.87, 151.21,  60], [-37.81, 144.96,  40], [-27.47, 153.03,  20],
  [-31.95, 115.86,  15], [-36.85, 174.76,  15],
  // Latin America
  [-23.55, -46.63,  30], [-22.91, -43.17,  15], [-34.61, -58.38,  20],
  [-33.45, -70.67,  15], [ 4.71,  -74.07,  10], [-12.05, -77.04,   8],
  [19.43,  -99.13,  25],
  // Africa + Middle East
  [30.05,   31.24,  15], [33.57,   -7.59,  10], [-26.20,  28.04,  20],
  [-33.92,  18.42,  15], [ 6.52,    3.38,  10], [-1.29,   36.82,  10],
  [25.20,   55.27,  20], [32.08,   34.78,  20],
];

const pointInPolygonLL = (lat, lon, path) => {
  let inside = false;
  const n = path.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const latI = path[i][0], lonI = path[i][1];
    const latJ = path[j][0], lonJ = path[j][1];
    if (((latI > lat) !== (latJ > lat)) &&
        (lon < (lonJ - lonI) * (lat - latI) / (latJ - latI) + lonI)) {
      inside = !inside;
    }
  }
  return inside;
};

// Build an equirectangular canvas texture where each country polygon is
// filled with a shade of Bitcoin-orange whose brightness scales with the
// aggregated node weight of the cities inside that polygon. Every
// landmass gets a faint baseline fill so the globe reads as a complete
// world map even where there are no Bitcoin hotspots.
export const buildLandmassTexture = () => {
  const W = 2048, H = 1024;
  const canvas = document.createElement('canvas');
  canvas.width = W;
  canvas.height = H;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, W, H);

  const weights = new Array(COUNTRY_PATHS.length).fill(0);
  let maxWeight = 0;
  for (let p = 0; p < COUNTRY_PATHS.length; p++) {
    const path = COUNTRY_PATHS[p];
    let sum = 0;
    for (const city of NODE_CITY_WEIGHTS) {
      if (pointInPolygonLL(city[0], city[1], path)) sum += city[2];
    }
    weights[p] = sum;
    if (sum > maxWeight) maxWeight = sum;
  }
  const norm = maxWeight > 0 ? maxWeight : 1;

  const latLonToCanvas = (lat, lon) => [
    ((lon + 180) / 360) * W,
    ((90 - lat) / 180) * H,
  ];

  const pathCrossesAntimeridian = (path) => {
    for (let i = 1; i < path.length; i++) {
      if (Math.abs(path[i][1] - path[i - 1][1]) > 180) return true;
    }
    return false;
  };

  for (let p = 0; p < COUNTRY_PATHS.length; p++) {
    const path = COUNTRY_PATHS[p];
    if (path.length < 3) continue;
    if (pathCrossesAntimeridian(path)) continue;
    let polar = true;
    for (const pt of path) if (pt[0] > -60) { polar = false; break; }
    if (polar) continue;

    const intensity = weights[p] / norm;
    const lightness = 16 + intensity * 48;
    const alpha = 0.32 + intensity * 0.68;
    ctx.fillStyle = `hsla(30, 88%, ${lightness}%, ${alpha})`;

    ctx.beginPath();
    for (let i = 0; i < path.length; i++) {
      const [x, y] = latLonToCanvas(path[i][0], path[i][1]);
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.closePath();
    ctx.fill();
  }

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 4;
  return tex;
};
